/**
 * iPad-style tablet mockup — auto / portrait / landscape orientation.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { tabletFrame, screenContent } from "./_mockHelpers.js";

export const tabletMockup: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";
  const variantRaw = (inputs.variant ?? "auto").toLowerCase();
  const isVertical = ctx.format === "9:16";
  const portrait = variantRaw === "portrait" || (variantRaw === "auto" && isVertical);
  const tabletW = portrait ? (isVertical ? 700 : 540) : (isVertical ? 880 : 980);
  const tabletH = portrait ? Math.round(tabletW * 1.36) : Math.round(tabletW * 0.74);

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const deviceId = `${clipPrefix}-device`;
  const glowId = `${clipPrefix}-glow`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const inner = screenContent({ media: inputs.media, accent, fallback: "app", objectFit: "contain" });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:${isVertical ? "column" : "row"};align-items:center;justify-content:center;gap:${isVertical ? 36 : 72}px;padding:72px;perspective:1800px;">
    <div style="display:flex;flex-direction:column;align-items:${isVertical ? "center" : "flex-start"};gap:18px;${isVertical ? "" : "flex:1;max-width:560px;"};text-align:${isVertical ? "center" : "left"};">
      ${caption ? `<div id="${capId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(caption)}</div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.82}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
    </div>
    <div style="position:relative;display:flex;justify-content:center;">
      <div id="${glowId}" style="position:absolute;inset:-30px;border-radius:80px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.42)} 0%, transparent 72%);filter:blur(24px);"></div>
      ${tabletFrame({ id: deviceId, width: tabletW, height: tabletH, accent, inner })}
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, y: 50, scale: 0.94, rotationY: portrait ? 10 : 0 }),
    gsapSetLine(`#${glowId}`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, y: 0, scale: 1, rotationY: 0, duration: 1.05, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${glowId}`, { opacity: 0.9, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.25),
    t.to(`#${deviceId}`, { y: -6, duration: Math.max(1.0, clipDurationSec - 1.6), ease: "raw:\"sine.inOut\"" }, 1.1),
  ];
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.25));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.45));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
